import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaFileInvoiceDollar, // For RTI
  FaTruckPickup, // For Roadside Assistance
  FaOilCan, // For Consumables Cover
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
  FaCarSide,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const MahindraCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question:
        "Is zero depreciation cover good for Mahindra SUVs like Thar and Scorpio?",
      answer:
        "Yes, it helps avoid depreciation deductions on parts during claims, especially for new vehicles.",
    },
    {
      question: "Can I insure a used Mahindra Bolero?",
      answer:
        "Yes, used Mahindra vehicles are eligible for both third-party and comprehensive insurance.",
    },
    {
      question: "What is the best policy for a new Mahindra XUV700?",
      answer:
        "A comprehensive policy with RTI, engine protection and zero depreciation is ideal.",
    },
    {
      question: "Is online insurance purchase available for Mahindra cars?",
      answer:
        "Yes, you can easily compare, buy and renew your Mahindra insurance online.",
    },
    {
      question: "Which Mahindra model is cheapest to insure?",
      answer:
        "Bolero and XUV300 generally have the lowest premiums due to simpler build and affordable parts.",
    },
    {
      question: "Do diesel Mahindra variants have higher premiums than petrol?",
      answer: "Usually yes, because of higher IDV and engine cost.",
    },
    {
      question: "What happens to insurance if I sell my Mahindra car?",
      answer:
        "You can transfer the policy to the new owner or cancel and claim a refund on the unused amount.",
    },
    {
      question: "Do I need to declare modifications on my Mahindra car?",
      answer:
        "Yes, accessories like bull bars or roof racks must be declared to avoid claim issues.",
    },
    {
      question: "What does a comprehensive Mahindra policy cover?",
      answer:
        "It includes own damage, third-party liability, theft, fire, natural calamities and more.",
    },
    {
      question: "Can I transfer NCB to a new Mahindra car?",
      answer:
        "Yes, your No Claim Bonus is transferable if the new car is in your name.",
    },
    {
      question: "What is RTI in Mahindra insurance?",
      answer:
        "Return to Invoice ensures full invoice value reimbursement in case of total loss or theft.",
    },
    {
      question: "Are tyres and alloys covered under insurance?",
      answer:
        "Only accident-related damage is covered. Use Tyre Protect for full tyre and rim protection.",
    },
    {
      question: "Do insurers offer cashless repairs for Mahindra cars?",
      answer:
        "Yes, most insurers have tie-ups with Mahindra-authorized service centres.",
    },
    {
      question: "Can I renew expired Mahindra insurance?",
      answer:
        "Yes, though a vehicle inspection may be required if the policy has lapsed for long.",
    },
    {
      question: "Is it legal to drive a Mahindra without insurance?",
      answer:
        "No, it's a punishable offence with fines up to ₹4,000 and possible license suspension.",
    },
    {
      question: "Can I change insurers before my Mahindra policy ends?",
      answer:
        "Generally only at renewal. Mid-policy changes are rarely allowed.",
    },
    {
      question: "Does Mahindra offer its own insurance plans?",
      answer:
        "Yes, through Mahindra Insurance Brokers, but you should compare all options for better deals.",
    },
    {
      question: "How long does it take to settle a Mahindra insurance claim?",
      answer:
        "Typically 7 to 14 days if documents are in order and damage is properly assessed.",
    },
    {
      question: "Is engine protection necessary for a Mahindra diesel vehicle?",
      answer:
        "Yes, especially for SUVs frequently driven in water-prone or rough conditions.",
    },
    {
      question:
        "Are electric Mahindra vehicles covered under regular insurance?",
      answer:
        "Yes, insurers now offer specific EV insurance for models like the e-Verito or future launches.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description:
        "Important for models like Scorpio-N, Thar and XUV700 to avoid out-of-pocket expenses on repairs.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine and Gearbox Protection",
      description: "Useful in areas prone to waterlogging or for off-road use.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice (RTI)",
      description: "Highly recommended for new XUV700 or Thar buyers.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description:
        "Covers breakdowns, towing and basic repairs on highways and remote routes.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description:
        "Helps with the cost of small parts like nuts, bolts, oils and filters during accident repairs.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];
const mahindrainsurance = [
    {
      name: 'Scorpio N',
      path: '/mahindra-scorpion-car-insurance',
      image: '/assets/mahindrascorpio.jpeg',
    },
     {
      name: 'XUV 700',
      path: '/mahindra-xuv700-car-insurance',
      image: '/assets/mahindraxuv700.jpeg',
    },
     {
      name: 'Thar',
      path: '/mahindra-thar-car-insurance',
      image: '/assets/mahindrathar.jpeg',
    },
     {
      name: 'Balero Neo',
      path: '/mahindra-baleroneo-car-insurance',
      image: '/assets/mahindrabaleroneo.jpeg',
    },
     {
      name: 'XUV 300',
      path: '/mahindra-xuv300-car-insurance',
      image: '/assets/mahindraxuv300.jpeg',
    },
    {
      name: 'Bolero',
      path: '/mahindra-bolero-car-insurance',
      image: '/assets/mahindrabolero.jpeg',
    },
    {
      name: 'Marazzo',
      path: '/mahindra-marazzo-car-insurance',
      image: '/assets/mahindrmarazzo.jpeg',
    },
    {
      name: 'XUV 400',
      path: '/mahindra-xuv400-car-insurance',
      image: '/assets/mahindrxuv400.jpeg',
    },
  ];
  return (
    <>
      <Helmet>
        <title>
          Mahindra Car Insurance in India – Compare Quotes & Buy Online
        </title>
        <meta
          name="description"
          content="Explore Mahindra car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Mahindra vehicle."
        />
        <meta
          name="keywords"
          content="mahindra car insurance, auto insurance india, mahindra thar insurance, mahindra xuv700 insurance, car insurance quotes"
        />
         <link rel="canonical" href="https://thefinmaster.com/mahindra-car-insurance" />
       <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
       
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Mahindra Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">
                    Mahindra Car Insurance
                  </span>{" "}
                 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Mahindra is known for building tough, reliable vehicles that can handle both city roads and challenging terrains. Whether you drive a compact KUV100 or a powerful XUV700, keeping your car protected starts with the right insurance. This guide takes you through everything you need to know about insuring a Mahindra vehicle, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.

                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/mahindrainsurance.jpeg"
                alt="Mahindra Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Mahindra in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Mahindra & Mahindra has been a pioneer in the Indian automobile
            industry for decades. The brand is known for manufacturing vehicles
            that cater to Indian driving conditions, with a focus on durability,
            safety and innovation. Mahindra’s portfolio includes compact SUVs,
            full-sized SUVs and utility vehicles that appeal to both urban
            drivers and rural users.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Popular models like the Scorpio, XUV700 and Bolero have a strong
            reputation for power and resilience. If you drive a Mahindra, having
            a tailored insurance plan is important for both legal compliance and
            long-term financial security.
          </p>
        </div>
      </div>

        <div className="p-4 sm:p-8 bg-white">
                        <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                          Choose Your Car Brand
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {mahindrainsurance.map((calc, index) => (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.04 }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <Link
                          to={calc.path}
                          className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                        >
                          <img
                            src={calc.image}
                            alt={calc.name}
                            className="w-16 h-16 object-contain mb-2"
                          />
                          <span className="text-md font-medium text-gray-800 break-words leading-snug">
                            {calc.name}
                          </span>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                  </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Mahindra Owners Need a Thoughtful Insurance Plan
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Mahindra vehicles often come with high-performance engines and
            durable build quality. However, due to their premium components and
            off-road capabilities, repairs and part replacements can be costly.
            A good insurance plan helps protect against:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Road accidents and collisions</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Theft and vehicle break-ins</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Damage from floods, storms or landslides</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Third-party liability claims</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>
                Expensive repairs for SUVs and advanced safety systems
              </span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            Insurance also provides access to a network of authorized garages,
            cashless repairs and flexible add-on covers tailored to your
            Mahindra model.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Mahindra Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here’s a glance at Mahindra’s top-selling cars in India, along with
          estimated insured declared value (IDV) and starting premiums:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "Thar",
                category: "Off-Road SUV",
                idv: "12–17 Lakh",
                premium: "₹4,500 to ₹7,200",
              },
              {
                model: "XUV700",
                category: "Premium SUV",
                idv: "17–26 Lakh",
                premium: "₹5,000 to ₹8,500",
              },
              {
                model: "Scorpio-N",
                category: "Full-Size SUV",
                idv: "16–24 Lakh",
                premium: "₹4,800 to ₹7,900",
              },
              {
                model: "Bolero",
                category: "Utility SUV",
                idv: "9–13 Lakh",
                premium: "₹3,200 to ₹5,300",
              },
              {
                model: "XUV300",
                category: "Compact SUV",
                idv: "8–12 Lakh",
                premium: "₹3,000 to ₹4,800",
              },
              {
                model: "Marazzo",
                category: "MUV",
                idv: "14–17 Lakh",
                premium: "₹4,000 to ₹6,500",
              },
              {
                model: "Bolero Neo",
                category: "Compact Utility",
                idv: "10–12 Lakh",
                premium: "₹3,400 to ₹5,200",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Thar", "Off-Road SUV", "12–17 Lakh", "₹4,500 to ₹7,200"],
                  ["XUV700", "Premium SUV", "17–26 Lakh", "₹5,000 to ₹8,500"],
                  [
                    "Scorpio-N",
                    "Full-Size SUV",
                    "16–24 Lakh",
                    "₹4,800 to ₹7,900",
                  ],
                  ["Bolero", "Utility SUV", "9–13 Lakh", "₹3,200 to ₹5,300"],
                  ["XUV300", "Compact SUV", "8–12 Lakh", "₹3,000 to ₹4,800"],
                  ["Marazzo", "MUV", "14–17 Lakh", "₹4,000 to ₹6,500"],
                  [
                    "Bolero Neo",
                    "Compact Utility",
                    "10–12 Lakh",
                    "₹3,400 to ₹5,200",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons That Make Sense for Mahindra Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Mahindra SUVs are designed for heavy use, long drives and rough roads.
          To enhance your coverage, consider the following add-ons:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Companies for Mahindra Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          The following insurers offer wide garage networks, fast claims and
          custom plans suitable for Mahindra owners:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Great for high-value SUVs like XUV700 and Scorpio-N.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for digital claims and a large cashless garage network.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📋</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Flexible coverage with a variety of add-on options.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Reliance General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers affordable plans and online policy renewal.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💸</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              High claim settlement ratio with easy add-on customisation.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                How to Save on Mahindra Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Here are some effective ways to reduce your premium while keeping
              the coverage solid:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Compare insurance quotes online before finalising your policy.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Maintain your No Claim Bonus (NCB) for renewal discounts.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Choose a voluntary deductible to lower your premium.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Install certified anti-theft devices to qualify for insurer
                  discounts.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Renew your insurance before expiry to avoid inspection and
                  penalty.
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Mahindra Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To buy or renew Mahindra insurance, you will need:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Vehicle Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance policy (if applicable)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>PAN or Aadhaar number for KYC</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>
                  Passport-size photographs (for some offline policies)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Mahindra Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Mahindra cars are engineered for strength, performance and
                reliability. A comprehensive insurance plan gives you peace of
                mind whether you drive through city traffic or rugged trails.
                With the right insurer, relevant add-ons and timely renewal, you
                can enjoy smooth ownership and hassle-free claims.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you compare policies, discover savings
                and choose the best protection for your Mahindra vehicle.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default MahindraCarInsurance;
